/**
 * InsightPro™ — Company Context  v2
 * Stores selected company per UID — prevents cross-user localStorage bleed.
 *
 * Logo/name live-sync: the localStorage snapshot captures permissions/role/
 * branchScope at company-select time (those are membership-driven and stable
 * for the session), but the tenant logoUrl and display name are pulled
 * live from companies/{id} via onSnapshot. Without this, admin logo updates
 * only appeared after a logout/login cycle because the snapshot never got
 * refreshed. See Imraan's 2026-05-12 report on stale logo for context.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { auth } from '../utils/firebase';

const CompanyContext = createContext(null);

function storageKey() {
  const uid = auth.currentUser?.uid;
  return uid ? `insightpro_company_${uid}` : null;
}

function loadStored() {
  try {
    const key = storageKey();
    if (!key) return null;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function CompanyProvider({ children }) {
  const [selectedCompany, setSelectedCompanyState] = useState(() => loadStored());
  const [companyKey, setCompanyKey] = useState(0);

  // Live-sync logoUrl + name from companies/{id} so admin updates appear
  // immediately on the live screens (sidebar/header/etc.) without requiring
  // a logout. Permissions/role/branchScope are NOT touched — those remain
  // authoritative from the membership snapshot taken at company-select time.
  // Errors (mid-logout race, transient denies) are silently ignored.
  const companyId = selectedCompany?.id || selectedCompany?.companyId;
  useEffect(() => {
    if (!companyId) return;
    const db = getFirestore();
    const unsub = onSnapshot(
      doc(db, 'companies', companyId),
      (snap) => {
        if (!snap.exists()) return;
        const d = snap.data();
        setSelectedCompanyState(prev => {
          if (!prev) return prev;
          // No-op if both fields already match — avoids needless re-renders
          // and localStorage writes on every snapshot ack.
          const newLogo = d.logoUrl || null;
          const newName = d.name || d.tradingName || prev.companyName;
          if (prev.logoUrl === newLogo && prev.companyName === newName) return prev;
          const next = { ...prev, logoUrl: newLogo, name: d.name || prev.name, companyName: newName };
          try {
            const uid = auth.currentUser?.uid;
            if (uid) localStorage.setItem(`insightpro_company_${uid}`, JSON.stringify(next));
          } catch {}
          return next;
        });
      },
      () => {}
    );
    return () => unsub();
  }, [companyId]);

  const setSelectedCompany = (company) => {
    setSelectedCompanyState(company);
    setCompanyKey(k => k + 1);
    try {
      const key = storageKey();
      if (!key) return;
      if (company) {
        localStorage.setItem(key, JSON.stringify(company));
      } else {
        localStorage.removeItem(key);
      }
    } catch {}
  };

  const loadForCurrentUser = () => {
    const stored = loadStored();
    setSelectedCompanyState(stored);
    return stored;
  };

  const clearCompany = () => {
    try {
      const key = storageKey();
      if (key) localStorage.removeItem(key);
    } catch {}
    setSelectedCompanyState(null);
  };

  const clearAllCompanyData = () => {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith('insightpro_company')) keysToRemove.push(k);
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch {}
    setSelectedCompanyState(null);
  };

  return (
    <CompanyContext.Provider value={{
      selectedCompany,
      setSelectedCompany,
      clearCompany,
      clearAllCompanyData,
      loadForCurrentUser,
      companyKey,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider');
  return ctx;
}
